import { useState, type JSX } from "react";
import { LineChart } from "../charts.js";
import { compact, full, LABEL, money, rangeText, remain, ringWindow, shortStamp, useAnalytics, useHistory, win, type DashboardData, type Provider } from "../data.js";
import { Cents, DatePill, Fit, Icon, PageHead, Pill, Ring, Seg, tileBasis } from "../ui.js";

export function Detail({ provider, onBack, dashboard }: { provider: Provider; onBack: () => void; dashboard: DashboardData | null }): JSX.Element {
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState(false);
  const { data } = useAnalytics(days);
  const history = useHistory(provider, days, expanded ? 20 : 4);
  const analytics = data?.providers.find((item) => item.provider === provider);
  const current = dashboard?.providers.find((item) => item.provider === provider);
  const usage = current?.tokenUsage;
  const ring = ringWindow(current?.windows ?? []);
  const week = win(current?.windows ?? [], 10080);
  const tokens = (history?.tokenItems ?? []).filter((item) => item.provider === provider).slice(0, expanded ? 20 : 4);
  const other = provider === "claude" ? "codex" : "claude";

  const exportData = (): void => {
    if (!analytics) return;
    const blob = new Blob([JSON.stringify({ provider, days, exportedAt: new Date().toISOString(), analytics, recentUploads: tokens }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `usagehub-${provider}-${days}d.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const boxes: Array<{ label: string; text: string; node?: JSX.Element; tone?: string; fallback?: string }> = [
    { label: "累计 Token", text: compact(usage?.totalTokens ?? null) },
    { label: "今日 Token", text: full(usage?.todayTokens ?? null), fallback: compact(usage?.todayTokens ?? null) },
    { label: "累计费用", text: money(usage?.totalCostUsd ?? null), node: <Cents text={money(usage?.totalCostUsd ?? null)} />, tone: provider === "claude" ? "red" : "cyan" },
    { label: "今日费用", text: money(usage?.todayCostUsd ?? null) },
    { label: "额度重置", text: remain(week?.resetsAt ?? null) },
  ];

  return (
    <>
      <PageHead title={`${LABEL[provider]} 详情`} subtitle="详细的用量、费用和上报记录" onBack={onBack} right={<>
        <Seg items={[[7, "7天"], [30, "30天"]] as const} value={days} onChange={setDays} />
        <DatePill text={rangeText(days, data)} />
        <button className="cx-btn outline" disabled={!analytics} onClick={exportData}><Icon name="download" />导出</button>
      </>} />
      <div className="cx-detail-top">
        <Ring value={ring.value} label={ring.label} provider={provider} size={78} stroke={10} />
        {boxes.map((box, index) => (
          <div className={`cx-dbox ${index === 2 ? `hot ${provider}` : ""}`} key={box.label} style={{ flex: tileBasis(box.label, box.text, 11, 18, 22) }}>
            <Fit key={box.text} className={`v ${box.tone ?? ""}`} max={18} min={10} {...(box.fallback !== undefined ? { fallback: box.fallback } : {})}>{box.node ?? box.text}</Fit>
            <small>{box.label}</small>
          </div>
        ))}
      </div>
      <div className="cx-detail-bottom">
        <section className={`cx-card neon soft ${provider} cx-chart-card`}>
          <header className="cx-card-head small">
            <h2>Token 使用趋势</h2>
            <div className="cx-legend"><span><i className={provider} />Token</span><span title="费用使用右侧坐标轴"><i className={other} />费用</span></div>
          </header>
          <LineChart points={analytics?.points ?? []} provider={provider} />
        </section>
        <section className={`cx-card neon soft ${provider} cx-records`}>
          <header className="cx-card-head small">
            <h2>最近上报记录</h2>
            <button className="cx-link" onClick={() => setExpanded((value) => !value)}>{expanded ? "收起" : "查看全部"}</button>
          </header>
          <div className="cx-rtable">
            <div className="cx-rrow head"><span>时间</span><span>今日Token</span><span>累计Token</span><span>今日费用</span><span>状态</span></div>
            <div className="cx-rbody">
              {tokens.map((item) => (
                <div className="cx-rrow" key={item.observedAt}>
                  <span className="t">{shortStamp(item.observedAt)}</span>
                  <span>{full(item.todayTokens)}</span>
                  <span>{compact(item.totalTokens)}</span>
                  <span>{money(item.todayCostUsd)}</span>
                  <span><Pill tone="ok">成功</Pill></span>
                </div>
              ))}
              {!tokens.length && <div className="cx-empty">还没有上报记录。</div>}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
